import { Dexie, liveQuery } from "dexie";
import type { EntityTable, Table } from "dexie";
import type { GeoPoint } from "../domain/location";
import { sendableNow } from "./LocalStore";
import type { LocalEventsListener, LocalStore } from "./LocalStore";
import type {
  ClockSnapshot,
  CrewCache,
  LeaveAccountCache,
  LocalAttribution,
  LocalEvent,
  ProjectCache,
  ProjectPreferences,
  RoleSnapshot,
  RosterCache,
} from "./model";

/**
 * Reads the owners whose entries wait for submission from keys `[ownerSubject, syncStatus]` of a
 * compound index.
 *
 * @param keys - The unique keys of the index.
 * @returns The owner keys, each once, in ascending order.
 */
function pendingOwnersOf(keys: readonly unknown[]): string[] {
  const owners = new Set<string>();

  for (const key of keys) {
    const pair: readonly unknown[] | null = Array.isArray(key) ? (key as readonly unknown[]) : null;
    const owner = pair?.[0];

    if (typeof owner === "string" && pair?.[1] === "pending") {
      owners.add(owner);
    }
  }

  const ordered = [...owners].sort();

  return ordered;
}

/** A device setting. */
interface SettingRow {
  readonly key: string;
  readonly value: string;
}

/**
 * IndexedDB schema (Dexie). Version history must only grow; never change an existing version.
 */
export class TimeTrackingDatabase extends Dexie {
  /** Local events (outbox and recently confirmed events). */
  events!: EntityTable<LocalEvent, "id">;

  /** Server snapshots per person. */
  snapshots!: EntityTable<ClockSnapshot, "ownerSubject">;

  /** Device settings. */
  settings!: EntityTable<SettingRow, "key">;

  /** Project attributions (outbox, T-124). */
  attributions!: EntityTable<LocalAttribution, "id">;

  /** Cached projects per person (T-124). */
  projectCaches!: EntityTable<ProjectCache, "ownerSubject">;

  /** Favorites and recently used projects per person (T-124). */
  projectPreferences!: EntityTable<ProjectPreferences, "ownerSubject">;

  /** Last known roles of the signed-in account per tenant (F-05). */
  roleSnapshots!: EntityTable<RoleSnapshot, "ownerSubject">;

  /** Last known own leave account per person and leave year (T-163). */
  leaveAccounts!: Table<LeaveAccountCache, [string, number]>;

  /** Last known crew of a manager or HR administrator (T-170). */
  crewCaches!: EntityTable<CrewCache, "ownerSubject">;

  /** Last known „Mein Dienstplan“ per person (T-178). */
  rosterCaches!: EntityTable<RosterCache, "ownerSubject">;

  /**
   * Opens (or creates) the database.
   *
   * @param name - Database name.
   * @param options - Dexie options (tests inject fake IndexedDB).
   */
  constructor(name: string, options?: ConstructorParameters<typeof Dexie>[1]) {
    super(name, options);
    this.version(1).stores({
      events: "id, [ownerSubject+occurredAt], [ownerSubject+syncStatus]",
      snapshots: "ownerSubject",
      settings: "key",
    });
    this.version(2).stores({
      attributions: "id, [ownerSubject+createdAtMs], [ownerSubject+syncStatus]",
      projectCaches: "ownerSubject",
      projectPreferences: "ownerSubject",
    });
    // Adds a table only; existing data stays untouched, so no upgrade function is needed.
    this.version(3).stores({
      roleSnapshots: "ownerSubject, subject",
    });
    // Adds a table only (T-163).
    this.version(4).stores({
      leaveAccounts: "[ownerSubject+leaveYear], ownerSubject, subject",
    });
    // Adds a table only (T-170).
    this.version(5).stores({
      crewCaches: "ownerSubject, subject",
    });
    // Adds a table only (T-178).
    this.version(6).stores({
      rosterCaches: "ownerSubject, subject",
    });
  }
}

/**
 * How long a confirmed event on behalf of another person stays on the device (T-170): the crew on the
 * clock screen applies it to a crew loaded before its confirmation, until a newer crew reflects it.
 * LEGAL-REVIEW: data of other persons on the device, assumed necessary for offline crews (ADR-0139).
 */
const proxyEventRetentionMs = 24 * 60 * 60 * 1000;

/**
 * Orders events chronologically (instant, then identifier), like the backend.
 *
 * @param left - First event.
 * @param right - Second event.
 * @returns Negative, zero or positive.
 */
function byTimeline(left: LocalEvent, right: LocalEvent): number {
  const byTime = left.occurredAt - right.occurredAt;

  if (byTime !== 0) {
    return byTime;
  }

  const byId = left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
  return byId;
}

/**
 * Orders attributions in the order they were recorded (device time, then identifier).
 *
 * @param left - First attribution.
 * @param right - Second attribution.
 * @returns Negative, zero or positive.
 */
function byRecording(left: LocalAttribution, right: LocalAttribution): number {
  const byTime = left.createdAtMs - right.createdAtMs;

  if (byTime !== 0) {
    return byTime;
  }

  const byId = left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
  return byId;
}

/**
 * Returns empty preferences for a person.
 *
 * @param ownerSubject - Owner key.
 * @returns Preferences without favorites and recent projects.
 */
function emptyPreferences(ownerSubject: string): ProjectPreferences {
  const preferences: ProjectPreferences = { ownerSubject, favorites: [], recent: [] };
  return preferences;
}

/**
 * Tells whether an error means that the connection to IndexedDB is lost: WebKit reports it as
 * `UnknownError` (as a DOMException or wrapped by Dexie under the same name).
 *
 * @param error - The error.
 * @returns `true` for a lost connection.
 */
function isLostConnection(error: unknown): boolean {
  const lost =
    typeof error === "object" && error !== null && "name" in error && error.name === "UnknownError";

  return lost;
}

/**
 * {@link LocalStore} on IndexedDB via Dexie (ADR-0024).
 */
export class DexieLocalStore implements LocalStore {
  private readonly db: TimeTrackingDatabase;

  /** Device time in milliseconds; decides whether an event still waits for its location (F-102). */
  private readonly nowMs: () => number;

  /** Whether a sign-out cleared the store: writes are refused until {@link reopen} (F-57). */
  private closed = false;

  /**
   * Creates the store.
   *
   * @param db - The database.
   * @param nowMs - Device time in milliseconds (the system clock by default).
   */
  constructor(db: TimeTrackingDatabase, nowMs: () => number = () => Date.now()) {
    this.db = db;
    this.nowMs = nowMs;
  }

  /**
   * Runs a write in its own transaction unless the store is closed for a sign-out (F-57). A write
   * requested after {@link close} or {@link clearAll} is refused by the first check. The check
   * inside the transaction only catches a close in the same tick, between the request and the start
   * of the transaction (or while the database still opens). A write whose transaction began before
   * the clearing transaction is ordered before it by IndexedDB and cleared with the rest.
   *
   * @param tables - Tables the write changes.
   * @param write - The write.
   * @returns `true` if the write ran, `false` after a sign-out.
   */
  private async guardedWrite(tables: Table[], write: () => Promise<void>): Promise<boolean> {
    if (this.closed) {
      return false;
    }

    const ran = await this.db.transaction("rw", tables, async () => {
      if (this.closed) {
        return false;
      }

      await write();

      return true;
    });

    return ran;
  }

  /**
   * Runs an operation once more on a new connection when the browser lost the connection to
   * IndexedDB. Safari on iOS loses it after the page stayed in the background for a while; every
   * request then fails with `UnknownError` until the database is opened again, which kept the
   * sign-out from starting (F-126).
   *
   * @param operation - The operation; it must be safe to repeat.
   * @returns The result of the operation.
   */
  private async withReconnect<T>(operation: () => Promise<T>): Promise<T> {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      if (!isLostConnection(error)) {
        throw error;
      }

      this.db.close();
      await this.db.open();
      const retried = await operation();

      return retried;
    }
  }

  /** @inheritdoc */
  async addEvent(event: LocalEvent): Promise<void> {
    const added = await this.guardedWrite([this.db.events], async () => {
      await this.db.events.add(event);
    });

    if (!added) {
      // A record of a person is never dropped silently.
      throw new Error("The local store was cleared by a sign-out.");
    }
  }

  /** @inheritdoc */
  async updateEvents(events: readonly LocalEvent[]): Promise<void> {
    // Only the synchronization fields are written: a location attached meanwhile (T-175) or any other
    // change to the stored row must not be overwritten by the copy the sync run started with (F-40).
    await this.guardedWrite([this.db.events], async () => {
      for (const event of events) {
        const modified = await this.db.events.where("id").equals(event.id).modify({
          syncStatus: event.syncStatus,
          syncedAtMs: event.syncedAtMs,
          attempts: event.attempts,
          error: event.error,
          hints: event.hints,
          suggestedAction: event.suggestedAction,
        });

        if (modified === 0) {
          await this.db.events.put(event);
        }
      }
    });
  }

  /** @inheritdoc */
  async attachEventLocation(eventId: string, location: GeoPoint): Promise<boolean> {
    let changed = 0;
    await this.guardedWrite([this.db.events], async () => {
      changed = await this.db.events
        .where("id")
        .equals(eventId)
        .and((event) => event.syncStatus === "pending" && event.attempts === 0)
        .modify({ location, locationAwaitedUntilMs: null });
    });
    const attached = changed > 0;

    if (!attached) {
      await this.releaseEventLocation(eventId);
    }

    return attached;
  }

  /** @inheritdoc */
  async releaseEventLocation(eventId: string): Promise<void> {
    await this.guardedWrite([this.db.events], async () => {
      await this.db.events
        .where("id")
        .equals(eventId)
        .and((event) => (event.locationAwaitedUntilMs ?? null) !== null)
        .modify({ locationAwaitedUntilMs: null });
    });
  }

  /** @inheritdoc */
  async listEvents(ownerSubject: string): Promise<LocalEvent[]> {
    const events = await this.db.events
      .where("[ownerSubject+occurredAt]")
      .between([ownerSubject, Dexie.minKey], [ownerSubject, Dexie.maxKey])
      .toArray();
    const ordered = events.sort(byTimeline);

    return ordered;
  }

  /** @inheritdoc */
  async listPending(ownerSubject: string): Promise<LocalEvent[]> {
    const events = await this.db.events
      .where("[ownerSubject+syncStatus]")
      .equals([ownerSubject, "pending"])
      .toArray();
    const ordered = sendableNow(events.sort(byTimeline), this.nowMs());

    return ordered;
  }

  /** @inheritdoc */
  async listPendingOwners(): Promise<string[]> {
    const owners = await this.withReconnect(async () => {
      // Unique keys of the compound index: one pair of owner and state each, no record is read.
      const [eventKeys, attributionKeys] = await Promise.all([
        this.db.events.orderBy("[ownerSubject+syncStatus]").uniqueKeys(),
        this.db.attributions.orderBy("[ownerSubject+syncStatus]").uniqueKeys(),
      ]);
      const found = pendingOwnersOf([...eventKeys, ...attributionKeys]);

      return found;
    });

    return owners;
  }

  /** @inheritdoc */
  async countPending(ownerSubject: string): Promise<number> {
    const count = await this.withReconnect(async () => {
      const [events, attributions] = await Promise.all([
        this.db.events.where("[ownerSubject+syncStatus]").equals([ownerSubject, "pending"]).count(),
        this.db.attributions
          .where("[ownerSubject+syncStatus]")
          .equals([ownerSubject, "pending"])
          .count(),
      ]);
      const total = events + attributions;

      return total;
    });

    return count;
  }

  /** @inheritdoc */
  async dismissRejected(id: string): Promise<void> {
    await this.db.transaction("rw", this.db.events, async () => {
      const event = await this.db.events.get(id);

      if (event?.syncStatus === "rejected") {
        await this.db.events.delete(id);
      }
    });
  }

  /** @inheritdoc */
  async pruneSynced(ownerSubject: string, syncedBeforeMs: number): Promise<number> {
    const deleted = await this.db.events
      .where("[ownerSubject+syncStatus]")
      .equals([ownerSubject, "synced"])
      .filter((event) => {
        const keptUntilMs =
          event.employeeId === undefined || event.employeeId === null
            ? syncedBeforeMs
            : syncedBeforeMs - proxyEventRetentionMs;
        const prunable = event.syncedAtMs !== null && event.syncedAtMs < keptUntilMs;

        return prunable;
      })
      .delete();

    return deleted;
  }

  /** @inheritdoc */
  async getSnapshot(ownerSubject: string): Promise<ClockSnapshot | null> {
    const snapshot = (await this.db.snapshots.get(ownerSubject)) ?? null;

    return snapshot;
  }

  /** @inheritdoc */
  async putSnapshot(snapshot: ClockSnapshot): Promise<void> {
    await this.guardedWrite([this.db.snapshots], async () => {
      await this.db.snapshots.put(snapshot);
    });
  }

  /** @inheritdoc */
  async getSetting(key: string): Promise<string | null> {
    const row = await this.db.settings.get(key);
    const value = row?.value ?? null;

    return value;
  }

  /** @inheritdoc */
  async putSetting(key: string, value: string): Promise<void> {
    await this.guardedWrite([this.db.settings], async () => {
      await this.db.settings.put({ key, value });
    });
  }

  /** @inheritdoc */
  watchEvents(ownerSubject: string, listener: LocalEventsListener): () => void {
    const subscription = liveQuery(() => this.listEvents(ownerSubject)).subscribe({
      next: listener,
      error: () => {
        listener([]);
      },
    });
    const unsubscribe = (): void => {
      subscription.unsubscribe();
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  watchSnapshot(
    ownerSubject: string,
    listener: (snapshot: ClockSnapshot | null) => void,
  ): () => void {
    const subscription = liveQuery(() => this.getSnapshot(ownerSubject)).subscribe({
      next: listener,
      error: () => {
        listener(null);
      },
    });
    const unsubscribe = (): void => {
      subscription.unsubscribe();
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  async addAttribution(attribution: LocalAttribution): Promise<void> {
    const added = await this.guardedWrite([this.db.attributions], async () => {
      await this.db.attributions.add(attribution);
    });

    if (!added) {
      throw new Error("The local store was cleared by a sign-out.");
    }
  }

  /** @inheritdoc */
  async updateAttributions(attributions: readonly LocalAttribution[]): Promise<void> {
    await this.guardedWrite([this.db.attributions], async () => {
      await this.db.attributions.bulkPut([...attributions]);
    });
  }

  /** @inheritdoc */
  async listAttributions(ownerSubject: string): Promise<LocalAttribution[]> {
    const attributions = await this.db.attributions
      .where("[ownerSubject+createdAtMs]")
      .between([ownerSubject, Dexie.minKey], [ownerSubject, Dexie.maxKey])
      .toArray();
    const ordered = attributions.sort(byRecording);

    return ordered;
  }

  /** @inheritdoc */
  async listPendingAttributions(ownerSubject: string): Promise<LocalAttribution[]> {
    const attributions = await this.db.attributions
      .where("[ownerSubject+syncStatus]")
      .equals([ownerSubject, "pending"])
      .toArray();
    const ordered = attributions.sort(byRecording);

    return ordered;
  }

  /** @inheritdoc */
  async pruneSyncedAttributions(ownerSubject: string, syncedBeforeMs: number): Promise<number> {
    const deleted = await this.db.attributions
      .where("[ownerSubject+syncStatus]")
      .equals([ownerSubject, "synced"])
      .filter((item) => item.syncedAtMs !== null && item.syncedAtMs < syncedBeforeMs)
      .delete();

    return deleted;
  }

  /** @inheritdoc */
  async dismissRejectedAttribution(id: string): Promise<void> {
    await this.db.transaction("rw", this.db.attributions, async () => {
      const attribution = await this.db.attributions.get(id);

      if (attribution?.syncStatus === "rejected") {
        await this.db.attributions.delete(id);
      }
    });
  }

  /** @inheritdoc */
  watchAttributions(
    ownerSubject: string,
    listener: (attributions: readonly LocalAttribution[]) => void,
  ): () => void {
    const subscription = liveQuery(() => this.listAttributions(ownerSubject)).subscribe({
      next: listener,
      error: () => {
        listener([]);
      },
    });
    const unsubscribe = (): void => {
      subscription.unsubscribe();
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  async getProjectCache(ownerSubject: string): Promise<ProjectCache | null> {
    const cache = (await this.db.projectCaches.get(ownerSubject)) ?? null;

    return cache;
  }

  /** @inheritdoc */
  async putProjectCache(cache: ProjectCache): Promise<void> {
    await this.guardedWrite([this.db.projectCaches], async () => {
      await this.db.projectCaches.put(cache);
    });
  }

  /** @inheritdoc */
  watchProjectCache(
    ownerSubject: string,
    listener: (cache: ProjectCache | null) => void,
  ): () => void {
    const subscription = liveQuery(() => this.getProjectCache(ownerSubject)).subscribe({
      next: listener,
      error: () => {
        listener(null);
      },
    });
    const unsubscribe = (): void => {
      subscription.unsubscribe();
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  async getProjectPreferences(ownerSubject: string): Promise<ProjectPreferences> {
    const preferences =
      (await this.db.projectPreferences.get(ownerSubject)) ?? emptyPreferences(ownerSubject);

    return preferences;
  }

  /** @inheritdoc */
  async putProjectPreferences(preferences: ProjectPreferences): Promise<void> {
    await this.guardedWrite([this.db.projectPreferences], async () => {
      await this.db.projectPreferences.put(preferences);
    });
  }

  /** @inheritdoc */
  watchProjectPreferences(
    ownerSubject: string,
    listener: (preferences: ProjectPreferences) => void,
  ): () => void {
    const subscription = liveQuery(() => this.getProjectPreferences(ownerSubject)).subscribe({
      next: listener,
      error: () => {
        listener(emptyPreferences(ownerSubject));
      },
    });
    const unsubscribe = (): void => {
      subscription.unsubscribe();
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  async getRoleSnapshot(ownerSubject: string): Promise<RoleSnapshot | null> {
    const snapshot = (await this.db.roleSnapshots.get(ownerSubject)) ?? null;

    return snapshot;
  }

  /** @inheritdoc */
  async hasRoleSnapshot(subject: string): Promise<boolean> {
    const count = await this.db.roleSnapshots.where("subject").equals(subject).count();
    const stored = count > 0;

    return stored;
  }

  /** @inheritdoc */
  async putRoleSnapshot(snapshot: RoleSnapshot): Promise<void> {
    await this.guardedWrite([this.db.roleSnapshots], async () => {
      await this.db.roleSnapshots.where("subject").notEqual(snapshot.subject).delete();
      await this.db.roleSnapshots.put(snapshot);
    });
  }

  /** @inheritdoc */
  async retainRoleSnapshots(subject: string): Promise<void> {
    await this.db.roleSnapshots.where("subject").notEqual(subject).delete();
  }

  /** @inheritdoc */
  async getLeaveAccount(
    ownerSubject: string,
    leaveYear: number,
  ): Promise<LeaveAccountCache | null> {
    const cache = (await this.db.leaveAccounts.get([ownerSubject, leaveYear])) ?? null;

    return cache;
  }

  /** @inheritdoc */
  async putLeaveAccount(cache: LeaveAccountCache): Promise<void> {
    await this.guardedWrite([this.db.leaveAccounts], async () => {
      await this.db.leaveAccounts.where("subject").notEqual(cache.subject).delete();
      await this.db.leaveAccounts.put(cache);
    });
  }

  /** @inheritdoc */
  async retainLeaveAccounts(subject: string): Promise<void> {
    await this.db.leaveAccounts.where("subject").notEqual(subject).delete();
  }

  /** @inheritdoc */
  async getCrewCache(ownerSubject: string): Promise<CrewCache | null> {
    const cache = (await this.db.crewCaches.get(ownerSubject)) ?? null;

    return cache;
  }

  /** @inheritdoc */
  async putCrewCache(cache: CrewCache): Promise<void> {
    await this.guardedWrite([this.db.crewCaches], async () => {
      await this.db.crewCaches.where("subject").notEqual(cache.subject).delete();
      await this.db.crewCaches.put(cache);
    });
  }

  /** @inheritdoc */
  async deleteCrewCache(ownerSubject: string): Promise<void> {
    await this.db.crewCaches.delete(ownerSubject);
  }

  /** @inheritdoc */
  async retainCrewCaches(subject: string): Promise<void> {
    await this.db.crewCaches.where("subject").notEqual(subject).delete();
  }

  /** @inheritdoc */
  async getRosterCache(ownerSubject: string): Promise<RosterCache | null> {
    const cache = (await this.db.rosterCaches.get(ownerSubject)) ?? null;

    return cache;
  }

  /** @inheritdoc */
  async putRosterCache(cache: RosterCache): Promise<void> {
    await this.guardedWrite([this.db.rosterCaches], async () => {
      await this.db.rosterCaches.where("subject").notEqual(cache.subject).delete();
      await this.db.rosterCaches.put(cache);
    });
  }

  /** @inheritdoc */
  async deleteRosterCache(ownerSubject: string): Promise<void> {
    await this.db.rosterCaches.delete(ownerSubject);
  }

  /** @inheritdoc */
  async retainRosterCaches(subject: string): Promise<void> {
    await this.db.rosterCaches.where("subject").notEqual(subject).delete();
  }

  /** @inheritdoc */
  async addSyncedEvent(event: LocalEvent): Promise<void> {
    await this.guardedWrite([this.db.events], async () => {
      await this.db.events.add(event);
    });
  }

  /** @inheritdoc */
  close(): void {
    this.closed = true;
  }

  /** @inheritdoc */
  async clearAll(): Promise<void> {
    // Before the clearing transaction begins: every write requested from now on is refused.
    this.closed = true;

    try {
      await this.withReconnect(() => this.clearTables());
    } catch (error) {
      // Nothing was deleted: the person keeps a working store, the caller reports the failure.
      this.closed = false;
      throw error;
    }
  }

  /**
   * Deletes the rows of every table in one transaction.
   */
  private async clearTables(): Promise<void> {
    await this.db.transaction(
      "rw",
      [
        this.db.events,
        this.db.snapshots,
        this.db.settings,
        this.db.attributions,
        this.db.projectCaches,
        this.db.projectPreferences,
        this.db.roleSnapshots,
        this.db.leaveAccounts,
        this.db.crewCaches,
        this.db.rosterCaches,
      ],
      async () => {
        await Promise.all([
          this.db.events.clear(),
          this.db.snapshots.clear(),
          this.db.settings.clear(),
          this.db.attributions.clear(),
          this.db.projectCaches.clear(),
          this.db.projectPreferences.clear(),
          this.db.roleSnapshots.clear(),
          this.db.leaveAccounts.clear(),
          this.db.crewCaches.clear(),
          this.db.rosterCaches.clear(),
        ]);
      },
    );
  }

  /** @inheritdoc */
  reopen(): void {
    this.closed = false;
  }
}
